/**
 * Soft-safe Move ⑥: stepwise journey Soft helpers (M2/M3 chip unlock + Next).
 * Extracted from god `state.ts` — farthestStep / chip visibility / Next Soft-disable.
 *
 * Owns process Soft hand-sharing journey chrome only — not SoftGate honesty,
 * not share payloads, not GTO/solver metrics.
 *
 * Soft-refuse: no GTO / equity% / outs / win rates / FAQ / Soft 14 locale fill.
 * Soft-safe ①–⑤ + Soft-outside 2.1–2.4 + Soft 4 Quiet must not regress.
 */
import type { HandState, StepId } from './types'

/**
 * Canonical step order for Soft stepwise chip journey (M2).
 * Softwise M10b: no separate board step — board cards are asked inline on the
 * Line step between streets (memory order).
 */
/**
 * Softwise M10c: the first question is the seat — `setup` (table / blinds /
 * stack) is no longer a journey step; it is a side screen opened from the
 * quiet defaults segment on the stacked line. Defaults: 6-max · 0.5/1 · 100bb.
 */
export const JOURNEY_STEP_IDS: readonly StepId[] = [
  'position',
  'cards',
  'actions',
  'summary',
]

/**
 * Soft stepwise M2: farthest step index reached this hand (0 = setup).
 * Chips show current + visited + immediate +1 only; reset on New hand.
 */
let farthestStepIndex = 0

/** Soft stepwise M2: farthest step index this hand (0 = seat). */
export function getFarthestStepIndex(): number {
  return farthestStepIndex
}

/** Soft stepwise: reset journey chips (New hand). */
export function resetJourneyFarthest(): void {
  farthestStepIndex = 0
}

/**
 * Soft stepwise M2: advance farthest when navigating / restoring / loading.
 * Idempotent — never shrinks.
 */
export function noteJourneyStep(step: StepId): void {
  const idx = JOURNEY_STEP_IDS.indexOf(step)
  if (idx > farthestStepIndex) farthestStepIndex = idx
}

/** Soft stepwise M2: target is current, already-visited, or immediate +1. */
export function isStepChipInJourney(target: StepId): boolean {
  // Softwise M10c: settings side screen is always reachable (defaults segment).
  if (target === 'setup') return true
  const i = JOURNEY_STEP_IDS.indexOf(target)
  return i >= 0 && i <= farthestStepIndex + 1
}

/** Soft stepwise: intentional skip (Quiet skip-to-cards) unlocks through target. */
export function unlockStepsThrough(step: StepId): void {
  noteJourneyStep(step)
}

/** Soft stepwise M3: hero has both hole cards. */
export function heroHolesComplete(hand: HandState): boolean {
  return Boolean(hand.holeCards[0] && hand.holeCards[1])
}

/** Soft stepwise M3: Next soft-disabled on cards/actions L1 gaps. */
export function isNextSoftDisabled(step: StepId, hand: HandState): boolean {
  if (step === 'cards') return !heroHolesComplete(hand)
  if (step === 'actions') return hand.actions.length === 0
  return false
}
